import { GridFields, InfoCard } from 'components';
import { isGlobalProject } from 'config';
import { lodash } from 'helpers';
import { useTranslation } from 'libs/i18n';
import { Chip, MenuOption } from 'libs/ui';
import { Icon } from 'libs/ui/Icon';
import { Colors } from 'libs/ui/theme';
import {
  CountryRiskBadges,
  CountryRiskField,
  CountryRiskRole,
} from 'modules/client/common/feature';

import { FirstHeadItem, FirstHeadOptionType } from './types';

export interface FirstHeadsInfoCardProps {
  items: FirstHeadItem[];
  editable?: boolean;
  onClickDelete?: (row: FirstHeadItem) => void;
  onClickFirstHead: (row: FirstHeadItem) => void;
  onClickArchive?: (row: FirstHeadItem) => void;
}

export const FirstHeadsInfoCard = ({
  items,
  onClickFirstHead,
  onClickArchive,
  onClickDelete,
  editable,
}: FirstHeadsInfoCardProps) => {
  const { t } = useTranslation();

  const isGlobal = isGlobalProject();

  const shownOptions: Partial<Record<FirstHeadOptionType, boolean>> = {
    archive: !!onClickArchive,
    dearchive: !!onClickArchive,
    delete: !!onClickDelete,
  };

  const onClickMenuOption = (
    item: FirstHeadItem,
    option: MenuOption<FirstHeadOptionType>
  ) => {
    switch (option.value) {
      case FirstHeadOptionType.delete:
        if (item.id) {
          onClickDelete?.(item);
        }
        break;
      case FirstHeadOptionType.archive:
      case FirstHeadOptionType.dearchive:
        if (item.id) {
          onClickArchive?.(item);
        }
        break;
      default:
        break;
    }
  };

  return (
    <GridFields>
      {items.map((item) => (
        <InfoCard
          key={item.id}
          fields={[
            {
              label: t(
                'client.legal.legalClientLegalHead.firstHeads.columns.position'
              ),
              value: item.position,
            },
            {
              label: t('client.legal.companyHeads.columns.status'),
              value: (
                <Chip
                  size="xs"
                  text={
                    item.archived
                      ? t('common.archivedStatus')
                      : t('common.activeStatus')
                  }
                  variant={item.archived ? 'gray' : 'green'}
                />
              ),
            },
            isGlobal
              ? {
                  label: t(
                    'client.legal.legalClientLegalHead.firstHeads.columns.residentialCountry'
                  ),
                  value: item.residentialCountry?.name,
                  valueAdornment: (
                    <CountryRiskBadges
                      country={item.residentialCountry}
                      field={CountryRiskField.ResidentialAddress}
                      role={CountryRiskRole.LegalClientFirstHead}
                    />
                  ),
                }
              : {
                  label: t(
                    'client.legal.legalClientLegalHead.firstHeads.columns.registrationCountry'
                  ),
                  value: item.registrationCountry?.name,
                  valueAdornment: (
                    <CountryRiskBadges
                      country={item.registrationCountry}
                      field={CountryRiskField.RegistrationAddress}
                      role={CountryRiskRole.LegalClientFirstHead}
                    />
                  ),
                },
            {
              label: t('client.legal.companyHeads.columns.expirationDate'),
              value: item.expirationDate,
            },
          ]}
          menuOptions={
            editable
              ? lodash.compact([
                  shownOptions.archive &&
                    !item.archived && {
                      value: FirstHeadOptionType.archive,
                      label: t('common.archive'),
                      Icon: Icon.TimeSleep,
                    },
                  shownOptions.dearchive &&
                    item.archived && {
                      value: FirstHeadOptionType.dearchive,
                      label: t('common.dearchive'),
                      Icon: Icon.RotateRightCheck,
                    },
                  shownOptions.delete && {
                    value: FirstHeadOptionType.delete,
                    label: t('common.delete'),
                    Icon: Icon.Bucket,
                    color: Colors.red,
                  },
                ])
              : undefined
          }
          title={item.name}
          variant={item.archived ? 'gray' : 'default'}
          onClick={() => onClickFirstHead(item)}
          onClickMenuOption={(option) => onClickMenuOption(item, option)}
        />
      ))}
    </GridFields>
  );
};
